// WhatsApp is switched off site-wide until the company number is live.
// Flip this to true to bring back: the floating bubble, the footer pill, the
// green card in the "Talk to a veterinarian" section, and the Contact page
// button. Nothing else needs editing.
export const WHATSAPP_ENABLED = false;

// Same idea for the phone line: the number below is a placeholder, so every
// place that shows or dials it is hidden until a real company line exists.
// Flip to true to bring back the footer row, the Contact page card and the
// "Call us" card in the support section.
export const PHONE_ENABLED = false;

export const CONTACT = {
  phone: "+964 771 000 4747",
  phoneHref: "+9647710004747",
  whatsapp: "9647710004747",
  email: "info@al-abrad.com",
  address: {
    en: "Al-Khulani Square, near the Real Estate Registry, Baghdad, Iraq",
    ar: "ساحة الخلاني، قرب التسجيل العقاري، بغداد، العراق",
  },
  // Open every day except Friday. Written as Sat–Thu because that is how the
  // working week reads locally.
  hours: {
    en: "Sat – Thu, 8:00 AM – 2:00 PM · Closed Friday",
    ar: "السبت – الخميس، ٨:٠٠ ص – ٢:٠٠ م · الجمعة عطلة",
  },
  // Al-Khulani Square premises. Used for the embedded map and the directions
  // link on the Contact page.
  coords: { lat: 33.3324781008611, lng: 44.40621860761632 },
};

// `id` values must stay in sync with GOVERNORATES in backend/server.py — the
// API rejects anything it doesn't recognise.
export const GOVERNORATES = [
  { id: "baghdad", en: "Baghdad", ar: "بغداد" },
  { id: "basra", en: "Basra", ar: "البصرة" },
  { id: "nineveh", en: "Nineveh", ar: "نينوى" },
  { id: "erbil", en: "Erbil", ar: "أربيل" },
  { id: "sulaymaniyah", en: "Sulaymaniyah", ar: "السليمانية" },
  { id: "duhok", en: "Duhok", ar: "دهوك" },
  { id: "kirkuk", en: "Kirkuk", ar: "كركوك" },
  { id: "anbar", en: "Anbar", ar: "الأنبار" },
  { id: "babil", en: "Babil", ar: "بابل" },
  { id: "karbala", en: "Karbala", ar: "كربلاء" },
  { id: "najaf", en: "Najaf", ar: "النجف" },
  { id: "diyala", en: "Diyala", ar: "ديالى" },
  { id: "wasit", en: "Wasit", ar: "واسط" },
  { id: "maysan", en: "Maysan", ar: "ميسان" },
  { id: "dhi-qar", en: "Dhi Qar", ar: "ذي قار" },
  { id: "al-muthanna", en: "Al-Muthanna", ar: "المثنى" },
  { id: "al-qadisiyah", en: "Al-Qadisiyah", ar: "القادسية" },
  { id: "salah-al-din", en: "Salah al-Din", ar: "صلاح الدين" },
  { id: "halabja", en: "Halabja", ar: "حلبجة" },
];

// `manufacturer` must match the manufacturer string on the products in
// data/products.js exactly — it is what /products?manufacturer=… filters on.
export const PARTNERS = [
  { name: "Vaksindo", manufacturer: "Vaksindo" },
  { name: "United Animal Health", manufacturer: "United Animal Health" },
];

export const T = {
  en: {
    nav: {
      home: "Home",
      products: "Products",
      about: "About Us",
      support: "Technical Support",
      partners: "Partners",
      contact: "Contact",
      browse: "Browse Products",
      categories: "Categories",
      primary: "Primary navigation",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      skipToContent: "Skip to main content",
    },
    hero: {
      kicker: "Al-Abrad · Veterinary Trading · Iraq",
      l1: "Poultry health,",
      l2: "protected from",
      l3: "origin to farm.",
      sub: "We import and distribute certified poultry vaccines, veterinary medicines and feed supplements across Iraq — moved through an unbroken cold chain and backed by hands-on technical support.",
      cta1: "Browse Products",
      cta2: "Request Technical Support",
      slideLabel: "Show slide",
    },
    trust: {
      title: "Built for Iraq's poultry producers",
      items: [
        { value: 2023, suffix: "", animate: false, label: "Established" },
        { value: 17, suffix: "", label: "Products distributed" },
        { value: 19, suffix: "", label: "Governorates served" },
        { value: 2, suffix: "", label: "International partners" },
      ],
    },
    categories: {
      kicker: "What we distribute",
      title: "Two product lines, one standard of quality",
      sub: "From live and inactivated poultry vaccines to targeted feed nutrition — every product is sourced from certified international manufacturers.",
      view: "Explore",
      // The two smaller tiles beside the product categories. These point at
      // pages that already exist rather than at invented product lines.
      support: {
        title: "Technical advisory",
        tagline: "Vaccination programs, dosing and disease control — reviewed with our veterinarians.",
      },
      reach: {
        title: "Nationwide delivery",
        tagline: "All 19 governorates, on a cold chain built for poultry producers.",
      },
    },
    bandA: {
      kicker: "Cold chain",
      title: "Full potency, from the manufacturer to your farm gate",
      sub: "Every vaccine we carry is moved under continuous temperature control. Nothing is left to chance between the plant and the bird.",
      cta: "How we do it",
    },
    bandB: {
      kicker: "Technical support",
      title: "Healthy flocks, higher returns",
      sub: "Our veterinarians and agricultural engineers work alongside your team on vaccination programs built for your farm.",
      cta: "Talk to our team",
    },
    why: {
      kicker: "Why Al-Abrad",
      title: "Serious about the details that keep flocks healthy",
      items: [
        { t: "Unbroken cold chain", d: "Temperature-controlled from the manufacturer to your farm gate — vaccines arrive at full potency, every time." },
        { t: "Certified sources only", d: "We trade solely with certified international manufacturers. No grey-market products, no compromises on authenticity." },
        { t: "Technical advisory", d: "Our veterinarians and agricultural engineers support you on vaccination programs, dosing and disease control." },
        { t: "Nationwide delivery", d: "Reliable distribution to all 19 governorates of Iraq, with a network built for poultry producers." },
      ],
    },
    featured: {
      kicker: "Featured products",
      title: "Trusted by farms across the country",
      view: "Details",
    },
    about: {
      kicker: "Our story",
      title: "A young company, built on an old-fashioned idea: do it right.",
      p1: "Al-Abrad opened its doors in 2023 with a clear purpose: to become the source Iraqi poultry producers can rely on for authentic, fully potent vaccines and feed supplements — delivered without a single break in the cold chain.",
      p2: "We work directly with certified international manufacturers and invest in the logistics that protect product integrity, so farm operators can trust exactly what reaches their birds.",
      quote: "We don't sell boxes. We protect a farmer's flock, one honest shipment at a time.",
      quoteBy: "Founder, Al-Abrad Company",
      cta: "More about us",
    },
    partners: {
      kicker: "Our partners",
      title: "Sourced from manufacturers the world trusts",
      sub: "We represent and distribute products from certified international animal-health manufacturers.",
      viewProducts: "View products",
    },
    supportCta: {
      kicker: "Technical support",
      title: "Talk to a veterinarian directly",
      sub: "Whether you need product information, a vaccination program review, distributor terms or pricing — reach our technical team through the channels below.",
      call: "Call us",
      whatsapp: "WhatsApp us",
      email: "Email us",
      form: "Send an inquiry",
      formValue: "Contact form",
      inquiryTypes: ["Product information", "Technical support", "Becoming a distributor", "Pricing"],
    },
    footer: {
      about: "Al-Abrad Company for Trading Veterinary Medicines and Agricultural Pesticides — serving Iraq's poultry producers since 2023.",
      quickLinks: "Quick links",
      productCats: "Products",
      contact: "Contact",
      rights: "All rights reserved.",
      fullName: "Al-Abrad Co. for Veterinary Medicines & Agricultural Pesticides",
    },
    products: {
      title: "Products",
      sub: "Certified poultry vaccines, veterinary medicines, feed supplements and crop protection — distributed across Iraq.",
      search: "Search products…",
      allCategories: "All categories",
      allSpecies: "All species",
      filterCategory: "Category",
      filterSpecies: "Target species",
      results: "products",
      empty: "No products match your filters.",
      clear: "Clear filters",
      details: "View details",
      byPartner: "Products we import and distribute from this manufacturer.",
      madeBy: "Manufacturer",
      allProducts: "All products",
    },
    detail: {
      composition: "Composition",
      species: "Target species",
      dosage: "Dosage & administration",
      storage: "Storage",
      manufacturer: "Manufacturer",
      category: "Category",
      quote: "Request a Quote",
      back: "Back to products",
      related: "Related products",
      note: "Inquiry-based ordering — contact our team for pricing and availability.",
      viewImage: "View image",
      brochure: "Download brochure (PDF)",
    },
    notFound: {
      title: "This page could not be found",
      sub: "The page you are looking for may have moved, or the link may be out of date. Browse our product range or head back to the homepage.",
    },
    form: {
      title: "Send us an inquiry",
      sub: "Tell us what you need and our technical team will get back to you. Fields marked with an asterisk are required.",
      name: "Full name",
      phone: "Phone number",
      email: "Email (optional)",
      farm: "Farm or company (optional)",
      governorate: "Governorate",
      governoratePlaceholder: "Select a governorate",
      topic: "What is this about?",
      product: "Product of interest (optional)",
      message: "Your message",
      messagePlaceholder: "Flock size, age, current vaccination program, or whatever else helps us answer properly.",
      submit: "Send inquiry",
      sending: "Sending…",
      successTitle: "Inquiry received",
      successBody: "Thank you. Our technical team will be in touch shortly. For anything urgent, email us at info@al-abrad.com.",
      sendAnother: "Send another inquiry",
      errorGeneric: "Something went wrong sending your inquiry. Please try again, or email us at info@al-abrad.com.",
      errorRateLimit: "You have sent several inquiries recently. Please try again later, or email us at info@al-abrad.com.",
      required: "This field is required",
      invalidPhone: "Enter a phone number with at least 8 digits",
      invalidEmail: "Enter a valid email address",
      tooShort: "Please give us a little more detail (at least 10 characters)",
      topics: {
        "product-info": "Product information",
        "technical-support": "Technical support",
        distribution: "Becoming a distributor",
        pricing: "Pricing",
      },
    },
    aboutPage: {
      title: "About Al-Abrad",
      sub: "A veterinary trading company built on cold-chain integrity, authentic products and practical support for Iraqi farms.",
      missionK: "Our mission",
      mission: "To give Iraq's poultry producers dependable access to authentic, potent veterinary products — and the technical guidance to use them well.",
      valuesTitle: "What we stand for",
      values: [
        { t: "Authenticity", d: "Only certified sources. What's on the label is what's in the vial." },
        { t: "Integrity of the cold chain", d: "We protect potency from origin to farm gate, without exception." },
        { t: "Practical expertise", d: "Advice from people who understand working farms, not just textbooks." },
        { t: "Reliability", d: "Nationwide delivery you can plan a production cycle around." },
      ],
      teamTitle: "Technical team",
      teamSub: "Veterinarians and agricultural engineers supporting farms across Iraq.",
    },
    supportPage: {
      title: "Technical Support",
      sub: "Hands-on veterinary and agricultural advisory for poultry producers — from vaccination programs to disease response.",
      servicesTitle: "How we help",
      services: [
        { t: "Vaccination program design", d: "Tailored Newcastle, Gumboro and IB programs matched to your flock and region." },
        { t: "Dosing & administration guidance", d: "Correct routes, timing and handling to preserve vaccine potency." },
        { t: "Disease response support", d: "Fast technical advice when something looks wrong in the house." },
        { t: "Product selection", d: "Choosing the right supplements and medicines for your production goals." },
      ],
      reachTitle: "Reach our technical team",
    },
    contactPage: {
      title: "Contact",
      sub: "Reach Al-Abrad for product information, technical support, distributor terms or pricing.",
      infoTitle: "Contact details",
      directions: "Get directions",
      mapTitle: "Find us",
      channels: "Channels",
      addressLabel: "Address",
      hoursLabel: "Opening hours",
    },
    common: {
      langLabel: "عربي",
      getSupport: "Get support",
      switchLanguage: "التبديل إلى العربية — Switch to Arabic",
    },
  },

  ar: {
    nav: {
      home: "الرئيسية",
      products: "المنتجات",
      about: "من نحن",
      support: "الدعم الفني",
      partners: "شركاؤنا",
      contact: "تواصل معنا",
      browse: "تصفّح المنتجات",
      categories: "الأقسام",
      primary: "التنقّل الرئيسي",
      openMenu: "فتح القائمة",
      closeMenu: "إغلاق القائمة",
      skipToContent: "تخطَّ إلى المحتوى الرئيسي",
    },
    hero: {
      kicker: "الأبرد · تجارة بيطرية · العراق",
      l1: "صحة الدواجن،",
      l2: "محمية من",
      l3: "المصدر إلى المزرعة.",
      sub: "نستورد ونوزّع لقاحات الدواجن والأدوية البيطرية والمكمّلات الغذائية المعتمدة في عموم العراق — ضمن سلسلة تبريد غير منقطعة، وبدعم فني عملي على أرض الواقع.",
      cta1: "تصفّح المنتجات",
      cta2: "اطلب الدعم الفني",
      slideLabel: "عرض الشريحة",
    },
    trust: {
      title: "بُنيت من أجل منتجي الدواجن في العراق",
      items: [
        { value: 2023, suffix: "", animate: false, label: "سنة التأسيس" },
        { value: 17, suffix: "", label: "منتج موزّع" },
        { value: 19, suffix: "", label: "محافظة نخدمها" },
        { value: 2, suffix: "", label: "شركاء دوليون" },
      ],
    },
    categories: {
      kicker: "ماذا نوزّع",
      title: "خطّا منتجات، ومعيار جودة واحد",
      sub: "من لقاحات الدواجن الحيّة والمُعطّلة إلى التغذية الموجّهة للأعلاف — كل منتج مصدره مصانع دولية معتمدة.",
      view: "استكشف",
      support: {
        title: "الاستشارة الفنية",
        tagline: "برامج التحصين والجرعات ومكافحة الأمراض — بمراجعة أطبائنا البيطريين.",
      },
      reach: {
        title: "توصيل لكل العراق",
        tagline: "جميع المحافظات التسع عشرة، ضمن سلسلة تبريد مبنية لمنتجي الدواجن.",
      },
    },
    bandA: {
      kicker: "سلسلة التبريد",
      title: "فعالية كاملة، من المصنع حتى باب مزرعتك",
      sub: "كل لقاح نحمله يُنقل ضمن تحكّم حراري متواصل. لا شيء يُترك للصدفة بين المصنع والطائر.",
      cta: "كيف نفعل ذلك",
    },
    bandB: {
      kicker: "الدعم الفني",
      title: "قطعان سليمة، وعوائد أعلى",
      sub: "أطباؤنا البيطريون ومهندسونا الزراعيون يعملون إلى جانب فريقك على برامج تحصين مصمّمة لمزرعتك.",
      cta: "تحدّث إلى فريقنا",
    },
    why: {
      kicker: "لماذا الأبرد",
      title: "نهتم بالتفاصيل التي تحافظ على صحة القطيع",
      items: [
        { t: "سلسلة تبريد غير منقطعة", d: "تحكّم بالحرارة من المصنع حتى باب مزرعتك — تصل اللقاحات بكامل فعاليتها في كل مرة." },
        { t: "مصادر معتمدة فقط", d: "نتعامل حصراً مع مصانع دولية معتمدة. لا منتجات من السوق الرمادية، ولا تهاون في الأصالة." },
        { t: "استشارة فنية", d: "أطباؤنا البيطريون ومهندسونا الزراعيون يدعمونك في برامج التحصين والجرعات ومكافحة الأمراض." },
        { t: "توصيل لكل العراق", d: "توزيع موثوق لجميع المحافظات التسع عشرة، عبر شبكة مبنية لمنتجي الدواجن." },
      ],
    },
    featured: {
      kicker: "منتجات مختارة",
      title: "موثوقة لدى المزارع في عموم البلاد",
      view: "التفاصيل",
    },
    about: {
      kicker: "قصتنا",
      title: "شركة فتية، قامت على فكرة قديمة: أن نتقن العمل.",
      p1: "فتحت الأبرد أبوابها عام ٢٠٢٣ بهدفٍ واضح: أن تكون المصدر الذي يعتمد عليه منتجو الدواجن في العراق للحصول على لقاحات ومكمّلات علفية أصلية وكاملة الفعالية — تصل دون أي انقطاع في سلسلة التبريد.",
      p2: "نعمل مباشرةً مع مصانع دولية معتمدة، ونستثمر في الخدمات اللوجستية التي تحمي سلامة المنتج، ليطمئن مربّو الدواجن إلى ما يصل قطعانهم تماماً.",
      quote: "نحن لا نبيع علباً. نحن نحمي قطيع المربّي، بشحنة صادقة تلو الأخرى.",
      quoteBy: "مؤسس شركة الأبرد",
      cta: "المزيد عنّا",
    },
    partners: {
      kicker: "شركاؤنا",
      title: "من مصانع يثق بها العالم",
      sub: "نمثّل ونوزّع منتجات من مصانع صحة حيوانية دولية معتمدة.",
      viewProducts: "عرض المنتجات",
    },
    supportCta: {
      kicker: "الدعم الفني",
      title: "تحدّث إلى طبيب بيطري مباشرةً",
      sub: "سواء احتجت معلومات عن منتج، أو مراجعة برنامج تحصين، أو شروط توزيع، أو تسعير — تواصل مع فريقنا الفني عبر القنوات أدناه.",
      call: "اتصل بنا",
      whatsapp: "واتساب",
      email: "راسلنا",
      form: "أرسل استفساراً",
      formValue: "نموذج التواصل",
      inquiryTypes: ["معلومات عن منتج", "دعم فني", "أن تصبح موزّعاً", "التسعير"],
    },
    footer: {
      about: "شركة الأبرد لتجارة الأدوية البيطرية والمبيدات الزراعية — في خدمة منتجي الدواجن في العراق منذ ٢٠٢٣.",
      quickLinks: "روابط سريعة",
      productCats: "المنتجات",
      contact: "تواصل",
      rights: "جميع الحقوق محفوظة.",
      fullName: "شركة الأبرد لتجارة الأدوية البيطرية والمبيدات الزراعية",
    },
    products: {
      title: "المنتجات",
      sub: "لقاحات دواجن وأدوية بيطرية ومكمّلات غذائية ووقاية محاصيل معتمدة — موزّعة في عموم العراق.",
      search: "ابحث عن منتج…",
      allCategories: "كل الأقسام",
      allSpecies: "كل الأنواع",
      filterCategory: "القسم",
      filterSpecies: "النوع المستهدف",
      results: "منتج",
      empty: "لا توجد منتجات مطابقة للفلاتر.",
      clear: "مسح الفلاتر",
      details: "عرض التفاصيل",
      byPartner: "المنتجات التي نستوردها ونوزّعها من هذا المصنع.",
      madeBy: "المُصنِّع",
      allProducts: "كل المنتجات",
    },
    detail: {
      composition: "التركيب",
      species: "الأنواع المستهدفة",
      dosage: "الجرعة وطريقة الإعطاء",
      storage: "التخزين",
      manufacturer: "الشركة المصنّعة",
      category: "القسم",
      quote: "اطلب عرض سعر",
      back: "العودة إلى المنتجات",
      related: "منتجات ذات صلة",
      note: "الطلب عبر الاستفسار — تواصل مع فريقنا للتسعير والتوفّر.",
      viewImage: "عرض الصورة",
      brochure: "تحميل النشرة (PDF)",
    },
    notFound: {
      title: "تعذّر العثور على هذه الصفحة",
      sub: "ربما تكون الصفحة التي تبحث عنها قد نُقلت، أو أن الرابط لم يعد صالحاً. تصفّح منتجاتنا أو عُد إلى الصفحة الرئيسية.",
    },
    form: {
      title: "أرسل لنا استفسارك",
      sub: "أخبرنا بما تحتاجه وسيتواصل معك فريقنا الفني. الحقول المعلَّمة بنجمة إلزامية.",
      name: "الاسم الكامل",
      phone: "رقم الهاتف",
      email: "البريد الإلكتروني (اختياري)",
      farm: "المزرعة أو الشركة (اختياري)",
      governorate: "المحافظة",
      governoratePlaceholder: "اختر المحافظة",
      topic: "موضوع الاستفسار",
      product: "المنتج المطلوب (اختياري)",
      message: "رسالتك",
      messagePlaceholder: "حجم القطيع، العمر، برنامج التحصين الحالي، أو أي تفاصيل تساعدنا على الإجابة بدقّة.",
      submit: "إرسال الاستفسار",
      sending: "جارٍ الإرسال…",
      successTitle: "تمّ استلام استفسارك",
      successBody: "شكراً لك. سيتواصل معك فريقنا الفني قريباً. وللأمور العاجلة، راسلنا على info@al-abrad.com.",
      sendAnother: "إرسال استفسار آخر",
      errorGeneric: "تعذّر إرسال استفسارك. يرجى المحاولة مرة أخرى أو مراسلتنا على info@al-abrad.com.",
      errorRateLimit: "لقد أرسلت عدة استفسارات مؤخراً. يرجى المحاولة لاحقاً أو مراسلتنا على info@al-abrad.com.",
      required: "هذا الحقل مطلوب",
      invalidPhone: "أدخل رقم هاتف مكوّناً من ٨ أرقام على الأقل",
      invalidEmail: "أدخل بريداً إلكترونياً صحيحاً",
      tooShort: "يرجى إعطاؤنا تفاصيل أكثر (١٠ أحرف على الأقل)",
      topics: {
        "product-info": "معلومات عن المنتجات",
        "technical-support": "دعم فني",
        distribution: "الانضمام كموزّع",
        pricing: "الأسعار",
      },
    },
    aboutPage: {
      title: "عن الأبرد",
      sub: "شركة تجارة بيطرية قامت على سلامة سلسلة التبريد، والمنتجات الأصلية، والدعم العملي لمزارع العراق.",
      missionK: "مهمتنا",
      mission: "أن نوفّر لمنتجي الدواجن في العراق وصولاً موثوقاً لمنتجات بيطرية أصلية وفعّالة — مع الإرشاد الفني لاستخدامها على أكمل وجه.",
      valuesTitle: "ما نؤمن به",
      values: [
        { t: "الأصالة", d: "مصادر معتمدة فقط. ما على الملصق هو ما في القارورة." },
        { t: "سلامة سلسلة التبريد", d: "نحمي الفعالية من المصدر حتى باب المزرعة، دون استثناء." },
        { t: "خبرة عملية", d: "نصيحة من أناس يفهمون المزارع العاملة، لا الكتب فقط." },
        { t: "الموثوقية", d: "توصيل لكل العراق يمكنك بناء دورة إنتاج عليه." },
      ],
      teamTitle: "الفريق الفني",
      teamSub: "أطباء بيطريون ومهندسون زراعيون يدعمون المزارع في عموم العراق.",
    },
    supportPage: {
      title: "الدعم الفني",
      sub: "استشارات بيطرية وزراعية عملية لمنتجي الدواجن — من برامج التحصين إلى الاستجابة للأمراض.",
      servicesTitle: "كيف نساعد",
      services: [
        { t: "تصميم برامج التحصين", d: "برامج نيوكاسل والغامبورو والتهاب الشعب مصمّمة حسب قطيعك ومنطقتك." },
        { t: "إرشاد الجرعات والإعطاء", d: "الطرق والتوقيت والمناولة الصحيحة للحفاظ على فعالية اللقاح." },
        { t: "دعم الاستجابة للأمراض", d: "نصيحة فنية سريعة عند ظهور أي خلل في الحظيرة." },
        { t: "اختيار المنتج", d: "اختيار المكمّلات والأدوية المناسبة لأهداف إنتاجك." },
      ],
      reachTitle: "تواصل مع فريقنا الفني",
    },
    contactPage: {
      title: "تواصل معنا",
      sub: "تواصل مع الأبرد لمعلومات المنتجات أو الدعم الفني أو شروط التوزيع أو التسعير.",
      infoTitle: "بيانات التواصل",
      directions: "احصل على الاتجاهات",
      mapTitle: "موقعنا",
      channels: "القنوات",
      addressLabel: "العنوان",
      hoursLabel: "أوقات الدوام",
    },
    common: {
      langLabel: "EN",
      getSupport: "احصل على الدعم",
      switchLanguage: "Switch to English — التبديل إلى الإنجليزية",
    },
  },
};
